
import React, { useEffect, useMemo, useState } from "react"
import { SpinnerText } from "../../ui/Spinner"
import { checkTerms } from "../../../services/euipoApi"
import InfoBox from "../../ui/InfoBox"

type Entry = {
  conceptId?: string
  classNumber?: number
  text: string
}

interface DirectoryReviewProps {
  terms: Entry[]
  setTerms: React.Dispatch<React.SetStateAction<Entry[]>>
  onBack: () => void
  onSaved?: () => void
  initialSaved?: boolean
  disabled?: boolean
  validation?: Record<string, boolean | null>
  checking?: boolean
  validationError?: boolean
  liveMode?: boolean
}

export default function DirectoryReview({
  terms,
  setTerms,
  onBack,
  onSaved,
  initialSaved,
  disabled = false,
  validation: externalValidation,
  checking: externalChecking,
  validationError = false,
  liveMode = false,
}: DirectoryReviewProps) {
  const groupedByClass = useMemo(() => {
    return terms.reduce((groups, term) => {
      if (term.classNumber == null) return groups

      if (!groups[term.classNumber]) {
        groups[term.classNumber] = []
      }

      groups[term.classNumber].push(term.text)

      return groups
    }, {} as Record<number, string[]>)
  }, [terms])

  const [validation, setValidation] = useState<
    Record<string, boolean | null>
  >({})

  const [checking, setChecking] = useState(false)
  const [saved, setSaved] = useState(Boolean(initialSaved))
  const [localValidationError, setLocalValidationError] =
    useState(false)

  const activeValidation = externalValidation ?? validation
  const activeChecking = externalChecking ?? checking
  const activeValidationError =
    externalValidation !== undefined
      ? validationError
      : localValidationError

  useEffect(() => {
    setSaved(Boolean(initialSaved))
  }, [initialSaved])

  // Fallback validation if no external validation is provided.
  useEffect(() => {
    if (
      terms.length === 0 ||
      saved ||
      externalValidation !== undefined
    ) {
      return
    }

    let cancelled = false

    setChecking(true)
    setLocalValidationError(false)

    const classes = Object.entries(groupedByClass).map(
      ([cls, list]) => ({
        classNumber: Number(cls),
        terms: list,
      })
    )

    checkTerms(classes)
      .then((result) => {
        if (cancelled) return

        const map: Record<string, boolean | null> = {}

        for (const cls of result.goodsAndServices ?? []) {
          for (const term of cls.terms ?? []) {
            map[`${term.text}|${cls.classNumber}`] =
              term.harmonized ?? null
          }
        }

        setValidation(map)
      })
      .catch((error) => {
        if (cancelled) return

        console.error(
          "Fehler bei der Überprüfung:",
          error
        )

        setLocalValidationError(true)
      })
      .finally(() => {
        if (!cancelled) {
          setChecking(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [terms, groupedByClass, saved, externalValidation])

  function handleDelete() {
    if (
      disabled ||
      !window.confirm("Delete your directory?")
    ) {
      return
    }

    setTerms([])
    setValidation({})
    setSaved(false)

    sessionStorage.removeItem("checkedTerms")
    sessionStorage.removeItem("savedDirectory")

    window.dispatchEvent(
      new Event("savedDirectoryChanged")
    )

    window.dispatchEvent(
      new CustomEvent("sectionComplete", {
        detail: {
          section: "goods",
          complete: false,
        },
      })
    )

    onBack()
  }

  function handleSave() {
    if (disabled || terms.length === 0) return

    setSaved(true)

    sessionStorage.setItem(
      "savedDirectory",
      "true"
    )

    sessionStorage.setItem(
      "checkedTerms",
      JSON.stringify(terms)
    )

    onSaved?.()

    window.dispatchEvent(
      new Event("savedDirectoryChanged")
    )

    window.dispatchEvent(
      new CustomEvent("sectionComplete", {
        detail: {
          section: "goods",
          complete: true,
        },
      })
    )
  }

  if (saved) {
    return (
      <div className="rounded-xl bg-white p-4 sm:p-5">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold text-blue-900">
              Saved directory
            </h3>

            <p className="mt-1 text-xs text-slate-500">
              {Object.keys(groupedByClass).length} classes
              {" · "}
              {terms.length} terms
            </p>
          </div>

          {!disabled && (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  setSaved(false)
                  onBack()
                }}
                className="rounded-lg border border-blue-200 px-3 py-2 text-sm font-medium text-blue-700 hover:bg-blue-50"
              >
                Edit
              </button>

              <button
                type="button"
                onClick={handleDelete}
                className="rounded-lg border border-red-200 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
              >
                Delete
              </button>
            </div>
          )}
        </div>

        <div className="space-y-3">
          {Object.entries(groupedByClass).map(
            ([cls, list]) => (
              <div
                key={cls}
                className="rounded-lg border border-slate-200 bg-slate-50 p-3"
              >
                <h4 className="mb-2 text-sm font-semibold text-blue-900">
                  Class {cls}
                  <span className="font-normal text-slate-500">
                    {" · "}
                    {list.length} terms
                  </span>
                </h4>

                <ul className="list-disc space-y-1 pl-5 text-sm text-slate-700">
                  {list.map((term, index) => (
                    <li key={index}>{term}</li>
                  ))}
                </ul>
              </div>
            )
          )}
        </div>
      </div>
    )
  }

  const validCount = Object.values(activeValidation).filter(
    (value) => value === true
  ).length

  const reviewCount = Object.values(activeValidation).filter(
    (value) => value === false
  ).length

  return (
    <div className="space-y-4 rounded-xl bg-white p-3 sm:p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-blue-900">
            Your directory
          </h3>

          <p className="mt-1 text-xs text-slate-500">
            Review your selected terms before saving.
          </p>
        </div>

        <div className="flex flex-wrap gap-3 text-xs">
          <div className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-green-500" />

            <strong>{validCount} valid in HDB</strong>

            <InfoBox title="Valid in HDB">
              These terms are officially part of the
              Harmonized Database.
            </InfoBox>
          </div>

          <div className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-orange-400" />

            <strong>
              {reviewCount} review suggestions
            </strong>

            <InfoBox title="Review suggestions">
              Orange terms require manual review.
            </InfoBox>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950">
        <strong>
          Please review your directory before saving.
        </strong>

        <p className="mt-1 text-xs">
          Make sure all terms are correct and relevant.
          Prefer terms from the Harmonized Database.
        </p>
      </div>

      {activeChecking && (
        <p
          role="status"
          className="text-xs text-slate-500"
        >
          <SpinnerText>Checking terms against the Harmonized Database...</SpinnerText>
        </p>
      )}

      {activeValidationError && (
        <p
          role="alert"
          className="text-xs text-amber-700"
        >
          Validation currently unavailable. Your terms
          are still kept; please try again before saving.
        </p>
      )}

      <div className="space-y-3">
        {Object.entries(groupedByClass).map(
          ([cls, list]) => (
            <div
              key={cls}
              className="rounded-xl border border-slate-200 bg-slate-50 p-3"
            >
              <div className="mb-3 flex items-center justify-between gap-2">
                <h4 className="text-sm font-semibold text-blue-900">
                  Class {cls}

                  <span className="font-normal text-slate-500">
                    {" · "}
                    {list.length} terms
                  </span>
                </h4>

                <button
                  type="button"
                  disabled={disabled}
                  onClick={() =>
                    setTerms((prev) =>
                      prev.filter(
                        (term) =>
                          term.classNumber !== Number(cls)
                      )
                    )
                  }
                  className="text-xs font-medium text-red-600 hover:underline disabled:opacity-40"
                >
                  Remove class
                </button>
              </div>

              <div className="flex flex-wrap gap-2">
                {list.map((term, index) => {
                  const status =
                    activeValidation[`${term}|${cls}`]

                  const style =
                    status === true
                      ? "border-green-300 bg-green-50"
                      : status === false
                      ? "border-orange-300 bg-orange-50"
                      : "border-slate-200 bg-white"

                  return (
                    <span
                      key={`${term}-${index}`}
                      className={`inline-flex max-w-full items-center gap-2 rounded-lg border px-2.5 py-1.5 text-sm text-slate-800 ${style}`}
                    >
                      <span className="break-words">
                        {term}
                      </span>

                      <button
                        type="button"
                        disabled={disabled}
                        aria-label={`Remove ${term}`}
                        title="Remove term"
                        onClick={() =>
                          setTerms((prev) =>
                            prev.filter(
                              (item) =>
                                !(
                                  item.classNumber ===
                                    Number(cls) &&
                                  item.text === term
                                )
                            )
                          )
                        }
                        className="shrink-0 font-bold text-slate-500 hover:text-red-600 disabled:opacity-40"
                      >
                        ×
                      </button>
                    </span>
                  )
                })}
              </div>
            </div>
          )
        )}
      </div>

      <div className="flex flex-wrap justify-end gap-3 border-t border-slate-200 pt-4">
        {!liveMode && (
          <button
            type="button"
            onClick={onBack}
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Back
          </button>
        )}

        <button
          type="button"
          disabled={disabled || terms.length === 0}
          onClick={handleSave}
          className="rounded-lg bg-green-700 px-4 py-2 text-sm font-semibold text-white hover:bg-green-800 disabled:opacity-40"
        >
          Save directory
        </button>
      </div>
    </div>
  )
}
